import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, Shirt, DoorOpen, Car, Calendar, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories } from "@/lib/hooks";
import { PageHeader, StatusBadge, Empty } from "@/components/common";
import { PropertySelect } from "@/components/FormBits";
import { Field } from "@/components/PropertyDialog";
import { DateInput, DateTimeInput } from "@/components/DateInput";
import { ProviderBanner } from "@/pages/Tickets";
import { ReviewsPanel } from "@/components/Reviews";

export const SERVICES = { menage: Sparkles, linge: Shirt, accueil: DoorOpen, transport: Car };
const EMPTY = { property_id: "", unit_id: "", service: "menage", scheduled_at: "", description: "", pickup: "", destination: "" };

function ConciergeDialog({ tenant }) {
  const { t, fdatetime } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const { data: leases = [] } = useQuery({ queryKey: ["leases"], queryFn: () => fetcher("/leases"), enabled: tenant && open });
  const { data: prop } = useQuery({ queryKey: ["property", f.property_id], queryFn: () => fetcher(`/properties/${f.property_id}`), enabled: !!f.property_id });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    const when = new Date(f.scheduled_at).toISOString();
    try {
      await api.post("/tickets", { ...f, unit_id: f.unit_id || null, scheduled_at: when, kind: "conciergerie", category: f.service, urgency: "normale",
        title: `${label(f.service)} — ${fdatetime(when)}`, description: f.description || label(f.service) });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      setOpen(false); setF(EMPTY);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-concierge-button"><Plus size={16} className="mr-1" />{t("newRequest")}</Button></DialogTrigger>
      <DialogContent className="max-w-lg rounded-sm" data-testid="concierge-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("concierge")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(SERVICES).map(([k, Icon]) => (
            <button key={k} type="button" onClick={() => setF({ ...f, service: k })} data-testid={`service-${k}`}
              className={`flex flex-col items-center gap-1.5 rounded-sm border p-3 text-xs font-semibold transition-colors ${f.service === k ? "border-[#B8860B] bg-[#fdf8ec] text-[#996515]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
              <Icon size={18} />{label(k)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("property")}><PropertySelect value={f.property_id} onChange={(v) => setF({ ...f, property_id: v, unit_id: "" })} testId="concierge-property-select"
            options={tenant ? leases.map((l) => ({ id: l.property_id, name: `${l.property.name} · ${l.unit.reference}` })) : undefined} /></Field>
          <Field label={t("unit")}>
            <Select value={f.unit_id} onValueChange={set("unit_id")} disabled={!prop}><SelectTrigger data-testid="concierge-unit-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(prop?.units || []).filter((u) => !u.is_common).map((u) => <SelectItem key={u.id} value={u.id}>{u.reference}</SelectItem>)}</SelectContent></Select>
          </Field>
          <div className="col-span-2"><Field label={t("scheduledAt")}><DateTimeInput value={f.scheduled_at} onChange={set("scheduled_at")} data-testid="concierge-date-input" /></Field></div>
          {f.service === "transport" && <>
            <Field label={t("pickup")}><Input value={f.pickup} onChange={set("pickup")} placeholder="Aéroport Nice T2" data-testid="concierge-pickup-input" /></Field>
            <Field label={t("destination")}><Input value={f.destination} onChange={set("destination")} data-testid="concierge-destination-input" /></Field>
          </>}
          <div className="col-span-2"><Field label={t("description")}><Textarea value={f.description} onChange={set("description")} data-testid="concierge-description-input" /></Field></div>
        </div>
        <Button onClick={save} disabled={!f.property_id || !f.scheduled_at} className="rounded-sm bg-[#0F172A]" data-testid="concierge-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Concierge() {
  const { user } = useAuth();
  const { t, fdatetime } = usePrefs();
  const { label } = useCategories();
  const { data = [] } = useQuery({ queryKey: ["tickets", "conciergerie"], queryFn: () => fetcher("/tickets?kind=conciergerie"), refetchInterval: 10000 });
  return (
    <div data-testid="concierge-page">
      <PageHeader eyebrow={t("secOps")} title={t("concierge")}>{user.role !== "prestataire" && <ConciergeDialog tenant={user.role === "locataire"} />}</PageHeader>
      <ProviderBanner />
      {["admin", "supervision", "gestionnaire", "prestataire"].includes(user.role) && <ReviewsPanel />}
      {data.length === 0 && <Empty />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.map((tk) => {
          const Icon = SERVICES[tk.service] || Sparkles;
          return (
            <Link key={tk.id} to={`/tickets/${tk.id}`} className="card-lift block rounded-sm border border-slate-200 bg-white p-5" data-testid={`concierge-card-${tk.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#0F172A] text-[#E5C46B]"><Icon size={18} /></div>
                <StatusBadge value={tk.status} />
              </div>
              <h3 className="mt-3 font-display text-lg font-bold">{label(tk.service)}</h3>
              <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><Calendar size={12} />{fdatetime(tk.scheduled_at)}</div>
              <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} />{tk.property_name}{tk.unit_ref ? ` · ${tk.unit_ref}` : ""}</div>
              {tk.service === "transport" && <div className="mt-2 text-xs text-slate-700">{tk.pickup} → {tk.destination}</div>}
              <div className="mt-3 text-xs text-slate-500">{tk.assigned_name || "—"} <span className="font-data text-slate-300">{tk.number}</span></div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
